/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInitialProjectTemplate } from '@jira/platform__services-project-properties/src/services/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	MORE_PROJECTS_SEARCH,
	PROJECTS_NAVIGATION_ITEM,
} from '../../constants/analytics/action-subject-id.tsx';
import {
	DROPDOWN_ITEM,
	BUTTON,
	NAVIGATION_ITEM,
	SEARCH_INPUT,
} from '../../constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM_BUTTON,
	CUSTOM_ITEM,
	FIXED_ITEM,
	FIXED_ITEM_DROPDOWN_ACTION,
} from '../../constants/analytics/component-type.tsx';
import {
	MEATBALL_DROPDOWN,
	PROJECTS,
	MORE_PROJECTS,
	VIEW_ALL_PROJECTS,
	PROJECTS_SEARCH,
} from '../../constants/analytics/item-id.tsx';
import { PROJECT, BOARD, CHANGE_VIEW } from '../../constants/analytics/item-type.tsx';
import { LEVEL_TWO } from '../../constants/analytics/level.tsx';
import type { BoardParams, ProjectParams } from '../types.tsx';
import { getSectionByStarredOrRecent } from '../utils.tsx';
import type { ProjectSectionType } from '../../constants/analytics/project-content-section.tsx';
import type { MoreProjectsSectionType } from '../../constants/analytics/more-projects-section-type.tsx';

export const useProjectsAnalyticsL2 = (): {
	sendMeatBallDropDownManageProjectsAnalyticsEvent: () => void;
	sendMeatBallDropDownOnProjectsAnalyticsEvent: (params: ProjectParams) => void;
	sendBoardNavigationItemInsideProjectsAnalyticsEvent: ({
		boardId,
		projectId,
		recent,
		starred,
		projectType,
	}: BoardParams) => void;
	sendCreateBoardOnBoardMenuItemAnalyticsEvent: ({
		recent,
		starred,
		projectId,
		projectType,
	}: ProjectParams) => void;
	sendViewAllBoardsAnalyticsEvent: ({
		projectId,
		projectType,
	}: {
		projectId: string;
		projectType: string | null | undefined;
	}) => void;
	sendChangeViewAnalyticsEvent: () => void;
	sendViewAllProjectsAnalyticsEvent: () => void;
	sendMoreProjectsFlyoutProjectAnalyticsEvent: ({
		projectId,
		sectionType,
	}: {
		projectId: string;
		sectionType: MoreProjectsSectionType;
	}) => void;
	sendProjectMenuContentSectionAnalyticsEvent: ({
		projectSection,
		canCreateProject,
	}: {
		projectSection: ProjectSectionType;
		canCreateProject: boolean | undefined | null;
	}) => void;
	sendMoreProjectsFlyoutProjectsSearchEnteredAnalyticsEvent: () => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialProjectTemplate = useInitialProjectTemplate();

	// `initialProjectTemplate` comes from a client-side project-property fetch
	// that resolves AFTER hydration (undefined -> value). Including it directly
	// in `commonAttributes` means that object's identity changes once the
	// property loads, which invalidates every analytics `useCallback` below and
	// cascades unnecessary re-renders into the sidebar menu items (measured via
	// Chippy). Since the template is only ever read at event-FIRE time (every
	// consumer spreads `commonAttributes` inside the callback body, and all
	// events fire on user interaction, well after load), we can read it lazily
	// via a ref so `commonAttributes` keeps a stable identity for the hook's
	// lifetime.
	const initialProjectTemplateRef = useRef(initialProjectTemplate);
	useEffect(() => {
		initialProjectTemplateRef.current = initialProjectTemplate;
	}, [initialProjectTemplate]);

	const commonAttributes = useMemo(
		() => ({
			level: LEVEL_TWO,
			// Getter reads the latest value at fire time, keeping object identity stable.
			get initialProjectTemplate() {
				return initialProjectTemplateRef.current;
			},
		}),
		// Intentionally empty: identity must remain stable; the template is read
		// lazily through the ref above.

		[],
	);

	const sendMeatBallDropDownManageProjectsAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: DROPDOWN_ITEM,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM_DROPDOWN_ACTION,
			itemId: 'manageProjects',
			parentItemId: MEATBALL_DROPDOWN,
		};
		fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// board navigation item under each project when project is expandable menu
	const sendBoardNavigationItemInsideProjectsAnalyticsEvent = useCallback(
		({ boardId, projectId, recent, starred, projectType }: BoardParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM,
				itemId: boardId,
				itemType: BOARD,
				parentItemId: projectId,
				parentItemType: PROJECT,
				projectType,
				section: getSectionByStarredOrRecent({ starred, recent }),
			};
			fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// meatball menu on projects
	const getMeatBallDropDownOnProjectsAnalytics = useCallback(
		({ recent, starred, projectId, projectType }: ProjectParams) => {
			return {
				...commonAttributes,
				actionSubjectId: PROJECTS_NAVIGATION_ITEM,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: MEATBALL_DROPDOWN,
				parentItemId: projectId,
				parentItemType: PROJECT,
				projectType,
				section: getSectionByStarredOrRecent({ starred, recent }),
			};
		},
		[commonAttributes],
	);

	/** @deprecated, replace with getMeatBallDropDownOnProjectsAnalytics  */
	const sendMeatBallDropDownOnProjectsAnalyticsEvent = useCallback(
		(params: ProjectParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: BUTTON,
			});

			const { actionSubjectId, ...attributes } = getMeatBallDropDownOnProjectsAnalytics(params);
			fireUIAnalytics(event, actionSubjectId, attributes);
		},
		[createAnalyticsEvent, getMeatBallDropDownOnProjectsAnalytics],
	);

	// create board button
	const sendCreateBoardOnBoardMenuItemAnalyticsEvent = useCallback(
		({ recent, starred, projectId, projectType }: ProjectParams): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: BUTTON,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM_BUTTON,
				itemId: 'createBoard',
				parentItemId: projectId,
				parentItemType: PROJECT,
				projectType,
				section: getSectionByStarredOrRecent({ starred, recent }),
			};
			fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// fixed item - view all boards link
	const sendViewAllBoardsAnalyticsEvent = useCallback(
		({
			projectId,
			projectType,
		}: {
			projectId: string;
			projectType: string | null | undefined;
		}): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: FIXED_ITEM,
				itemId: 'viewAllBoards',
				parentItemId: projectId,
				parentItemType: PROJECT,
				projectType,
			};
			fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	// fixed item - Change View in Meatball Menu on Projects section
	const sendChangeViewAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: DROPDOWN_ITEM,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM_DROPDOWN_ACTION,
			parentItemId: PROJECTS,
			itemId: CHANGE_VIEW,
		};
		fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// fixed item - View All Projects in More Projects flyout
	const sendViewAllProjectsAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
		});

		const attributes = {
			...commonAttributes,
			componentType: FIXED_ITEM_DROPDOWN_ACTION,
			parentItemId: MORE_PROJECTS,
			itemId: VIEW_ALL_PROJECTS,
		};
		fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	// custom item - project in More Projects flyout
	const sendMoreProjectsFlyoutProjectAnalyticsEvent = useCallback(
		({
			projectId,
			sectionType,
		}: {
			projectId: string;
			sectionType: MoreProjectsSectionType;
		}): void => {
			const event = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: NAVIGATION_ITEM,
			});

			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM,
				parentItemId: MORE_PROJECTS,
				itemType: PROJECT,
				itemId: projectId,
				moreProjectsSection: sectionType,
			};
			fireUIAnalytics(event, PROJECTS_NAVIGATION_ITEM, attributes);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	const sendProjectMenuContentSectionAnalyticsEvent = useCallback(
		({
			projectSection,
			canCreateProject,
		}: {
			projectSection: ProjectSectionType;
			canCreateProject: boolean | undefined | null;
		}): void => {
			const attributes = {
				...commonAttributes,
				componentType: CUSTOM_ITEM,
				itemId: projectSection,
				canCreateProject,
			};

			fireUIAnalytics(
				createAnalyticsEvent({}),
				'projectsNavigationContentSection rendered',
				attributes,
			);
		},
		[commonAttributes, createAnalyticsEvent],
	);

	const sendMoreProjectsFlyoutProjectsSearchEnteredAnalyticsEvent = useCallback((): void => {
		const event = createAnalyticsEvent({
			action: 'entered',
			actionSubject: SEARCH_INPUT,
		});

		const attributes = {
			...commonAttributes,
			parentItemId: MORE_PROJECTS,
			itemId: PROJECTS_SEARCH,
		};
		fireUIAnalytics(event, MORE_PROJECTS_SEARCH, attributes);
	}, [commonAttributes, createAnalyticsEvent]);

	return {
		sendMeatBallDropDownManageProjectsAnalyticsEvent,
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
		sendBoardNavigationItemInsideProjectsAnalyticsEvent,
		sendCreateBoardOnBoardMenuItemAnalyticsEvent,
		sendViewAllBoardsAnalyticsEvent,
		sendChangeViewAnalyticsEvent,
		sendViewAllProjectsAnalyticsEvent,
		sendMoreProjectsFlyoutProjectAnalyticsEvent,
		sendProjectMenuContentSectionAnalyticsEvent,
		sendMoreProjectsFlyoutProjectsSearchEnteredAnalyticsEvent,
	};
};
